/**
 * Core shared types for drawlol.
 *
 * Defined in M1.T2 per IMPLEMENTATION_PLAN §5.1 / §5.6 / §13.13.
 * Future milestones (M2-M5) may extend these but must NOT redefine the
 * fields the code depends on (e.g. `RoomState.kind`, `Player.joinedAt`,
 * `TurnSummary.finalSvgPath`).
 */

// ---------------------------------------------------------------------------
// Identifiers
// ---------------------------------------------------------------------------

export type PlayerId = string;
export type RoomId = string;
export type StrokeId = string;
export type GameId = string;
export type TurnId = string;

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

export type Point = { x: number; y: number };

// ---------------------------------------------------------------------------
// Brand palette
// ---------------------------------------------------------------------------

/**
 * The eight brand drawing colors (expanded from six per user feedback,
 * orange and purple added in the layout-polish pass). Stroke + fill
 * actions carry one of these as a literal hex string. Eraser is NOT a
 * member: see `StrokeEndAction.isEraser` for the eraser flag (chosen
 * over a sentinel literal so type narrowing stays clean for color-only
 * consumers like Picasso-card thumbnails).
 *
 * Wire-format mapping (§13.2.d, updated): colorIdx ∈ [0..7] maps to these
 * 8 hex strings in this exact order. colorIdx = 8 is the eraser sentinel.
 *
 * n10: There are intentionally **two distinct yellows** in the system:
 *  - `#ffd23f`: drawing palette yellow (BrandColor, one of the 8 draw colors)
 *  - `#ffe066`: UI highlighter/accent (CSS token `--highlighter` in tokens.css)
 *
 * They are deliberately different:
 *  - The drawing yellow is slightly warmer/darker, it reads clearly as a
 *    stroke color against the `--paper` (#fdf6e3) background.
 *  - The UI highlighter is brighter (passes WCAG AA at 13:1 with `--ink`)
 *    and is used for CTAs, the wordmark underline, and active-state accents.
 *
 * Do NOT consolidate them; the visual difference is intentional.
 */
export type BrandColor =
  | "#000000" // ink black
  | "#e63946" // red
  | "#ffd23f" // drawing yellow: distinct from --highlighter (#ffe066); see note above
  | "#4361ee" // blue
  | "#7cb518" // green
  | "#8b5a2b" // brown
  | "#f77f00" // orange (added: user feedback, layout-polish pass)
  | "#9d4edd"; // purple (added: user feedback, layout-polish pass)

/**
 * Runtime mirror of the 8-color BrandColor union, in palette index order
 * (matches `PALETTE_COLORS` in wire-binary.ts; colorIdx ∈ [0..7]).
 *
 * **One definition (M3.fix B7).** Before this lock there were three
 * divergent palettes:
 *  - shared/src/types.ts had the 8 canonical hexes (this list).
 *  - client/JoinRoomForm.tsx had 6 hexes including the off-palette `#ff6b6b`.
 *  - server/index.ts had 7 hexes including `#ff6b6b` + `#f4a261`.
 * Server + client now both import BRAND_COLORS from here; any drift to a
 * non-palette color is a compile error against `BrandColor`.
 */
export const BRAND_COLORS: ReadonlyArray<BrandColor> = [
  "#000000",
  "#e63946",
  "#ffd23f",
  "#4361ee",
  "#7cb518",
  "#8b5a2b",
  "#f77f00",
  "#9d4edd",
] as const;

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

/**
 * A player in a room. `joinedAt` is the server-side wall-clock millisecond
 * timestamp at which the player first joined this room; it is the canonical
 * tiebreaker for host migration (oldest remaining player becomes host).
 * Added per §13 patch (CEO: "host migration needs explicit signaling").
 */
export type Player = {
  id: PlayerId;
  name: string;
  color: string; // hex; one of the 8 brand colors
  joinedAt: number; // ms since epoch; tiebreaker for HOST_CHANGED
};

// ---------------------------------------------------------------------------
// Room shape
// ---------------------------------------------------------------------------

/**
 * RoomState.kind discriminator. `"freeDraw"` is implemented in M2; `"game"`
 * is implemented in M3 (transitions) + M4 (UI/logic). The kind is locked at
 * room creation and never mutates, there is no transition from one kind
 * to the other.
 */
export type RoomKind = "game" | "freeDraw";

/**
 * Game phases. `"lobby"` is the only legal phase when `kind === "freeDraw"`
 * (rooms are kind-locked; freeDraw rooms have no further transitions,
 * see §5.5 PHASE_END legality table).
 */
export type RoomPhase = "lobby" | "wordPick" | "drawing" | "reveal" | "gameOver" | "freeDraw";

export type WordTier = "easy" | "medium" | "hard";

export type RoomSettings = {
  rounds: number; // 1-5
  drawTimerSeconds: number; // 30 | 60 | 75 | 90 | 120
  wordPickTimerSeconds: number; // 10 | 15 | 20
  /**
   * Duration of the reveal phase (between drawing and the next wordPick or
   * gameOver) in whole seconds. M4 batch 5: replaces the server-side
   * hardcoded `revealDurationMs = 5000` constant so the host can tune
   * "scoreboard dwell time" via UPDATE_SETTINGS.
   *
   * Discrete values: 3 | 5 | 8, mirrors the other game-loop timers'
   * fixed-stops pattern (drawTimerSeconds, wordPickTimerSeconds). The initial default is 7.
   * UPDATE_SETTINGS in the reducer drops any out-of-range candidate so the
   * wire layer can't smuggle in a 0 or 120 to grief the game.
   */
  revealTimerSeconds: number; // selectable 3 | 5 | 8; initial default 7
  hintsEnabled: boolean;
  useCustomWords: boolean;
  customWords: ReadonlyArray<CustomWord>;
};

export type CustomWord = {
  word: string;
  tier: WordTier; // untagged custom words default to "medium" at parse
};

// ---------------------------------------------------------------------------
// Word-picker types
// ---------------------------------------------------------------------------

/**
 * A single word offered to the drawer at `wordPick` phase entry. The reducer
 * stashes a tuple of exactly 3 of these on `GameState.wordChoices`; the
 * `PICK_WORD` action body validates the selection against this tuple and
 * mints `GameState.currentTurn` from the matched entry.
 *
 * Canonical owner: this module. `wordPicker.ts` re-uses it; the index barrel
 * re-exports from here so consumers don't import a transitive name.
 */
export type WordChoice = { word: string; tier: WordTier };

// ---------------------------------------------------------------------------
// Per-turn rollup (carried in GameState; bounded; survives turns)
// ---------------------------------------------------------------------------

/**
 * Per §5.6 + §13.13. Stored in GameState.turnSummaries, bounded rollups
 * needed for end-of-game MVP rendering (Picasso card needs `finalSvgPath`
 * to render a thumbnail; Sleuth card needs `firstGuessLatencyMs` for
 * "fastest guess: 2.3s on 'pizza'").
 *
 * The Quote Generator MVP was cut in v3, so this carries no quotes.
 */
export type TurnSummary = {
  turnId: TurnId;
  drawerId: PlayerId;
  word: string;
  tier: WordTier;
  firstGuesserId?: PlayerId;
  /** Latency from drawing-phase start to the first correct guess. */
  firstGuessLatencyMs?: number;
  correctCount: number;
  drawerPoints: number;
  /** Final SVG path of the drawer's canvas, used to render MVP thumbnail. */
  finalSvgPath: string;
  abortedReason?: "drawer-disconnect" | "all-guessed" | "timeout";
  /**
   * Per-audience-member breakdown of correct guesses this turn (M4 batch
   * 6c). Order matches the order each guess landed (i.e. `order` ascending,
   * same as `CurrentTurn.guesses`). Wall-clock latency (`atMs`) is dropped
   *: it's per-turn timing not needed for the end-of-game scoreboard;
   * `firstGuessLatencyMs` already captures the only latency the Sleuth
   * card uses.
   *
   * Empty array when no audience member guessed correctly. Always defined
   * (never `undefined`) so the gameOver scoreboard helper can iterate
   * without a null-check.
   */
  guesses: ReadonlyArray<{ playerId: PlayerId; points: number; order: number }>;
};

// ---------------------------------------------------------------------------
// In-flight stroke (transport-side state, included for completeness)
// ---------------------------------------------------------------------------

/**
 * A finalized brush/eraser stroke in the ordered canvas op log.
 * `kind: "stroke"` discriminates it from a fill op (v4 T1 / D5).
 */
export type StrokeOp = {
  kind: "stroke";
  strokeId: StrokeId;
  drawerId: PlayerId;
  color: string;
  brushSize: number;
  startedAt: number;
  /** Final smoothed SVG path (set after STROKE_END). Used for reconnect sync. */
  path?: string;
  /** True if this stroke is an eraser stroke. */
  isEraser?: boolean;
};

/**
 * A flood-fill op in the ordered canvas op log (v4 T1, D5). Fills are no
 * longer a fire-and-forget local raster side effect: they are logged ops, so
 * they replicate to the audience and survive undo / clear / reconnect-hydrate.
 * Ordering matters: a fill samples whatever pixels sit beneath it, so it must
 * replay in sequence relative to the strokes around it.
 */
export type FillOp = {
  kind: "fill";
  /**
   * Op id. Mirrors a stroke's `strokeId` so the `Map<StrokeId, _>` container
   * plus the undo (pop-last-key) / clear machinery work on fills unchanged.
   */
  strokeId: StrokeId;
  drawerId: PlayerId;
  color: string;
  /** Hit point in CSS-pixel canvas space (the space strokes ride the wire in). */
  point: Point;
  startedAt: number;
};

/**
 * One entry in the ordered canvas op log (`RoomState.openStrokes`). A
 * discriminated union over brush/eraser strokes and flood fills. The
 * historical name `OpenStroke` is kept (it has many import sites) but it now
 * models any draw op, not just a stroke.
 */
export type OpenStroke = StrokeOp | FillOp;

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

export type ScoringConfig = {
  /** Difficulty multipliers for drawer points. Per PRD §3.7. */
  difficultyMultiplier: Readonly<Record<WordTier, number>>;
};

// ---------------------------------------------------------------------------
// GameState (populated when kind === "game" and phase !== "lobby")
// ---------------------------------------------------------------------------

export type DrawerDisconnect = {
  playerId: PlayerId;
  graceUntil: number; // ms since epoch
};

/**
 * Per-turn live state (in flight from PICK_WORD until drawing→reveal).
 *
 * Lifecycle (M4 batch 2):
 *  - **Initialized** by `PICK_WORD`: drawerId is `turnOrder[currentTurnIndex]`,
 *    word + tier come from the chosen `WordChoice`, `startedAt = 0`,
 *    `guesses = []`.
 *  - **Patched** by `PHASE_END(wordPick → drawing)`: `startedAt` is set to
 *    the action's wall-clock `t`.
 *  - **Appended** by `GUESS` with verdict `"exact"`: a new entry is pushed
 *    onto `guesses`.
 *  - **Cleared** by `PHASE_END(drawing → reveal)`: after the turn rollup is
 *    appended to `turnSummaries`, this field is removed.
 *
 * Drawer-only field `word` is the canonical (un-redacted) target. The wire
 * layer is responsible for stripping it before fanning out to audience
 * snapshots (§5.12).
 */
export type CurrentTurn = {
  /** Server-only scoring denominator, fixed when drawing starts. */
  audienceAtTurnStart?: number;
  drawerId: PlayerId;
  /** Canonical drawer-visible word. Wire layer redacts for audience (§5.12). */
  word: string;
  /**
   * Canonical length of `word` in code units (= `word.length`). Carried as a
   * separate field so the snapshot redactor can blank `word` to `""` for
   * audience-pending / spectator recipients without losing the length the
   * client needs to render a placeholder ("_ _ _ _ _"). The reducer always
   * populates this from the canonical word at PICK_WORD time; redaction is
   * purely a snapshot-time concern (§5.12 / §13.2.a).
   */
  wordLength: number;
  /**
   * Redaction-safe structural projection of `word`: every letter/digit → "_",
   * spaces + punctuation preserved verbatim (v4 T4, the "ice cream" bug). Lets
   * audience-pending / spectator clients render multi-word breaks ("___ _____")
   * without leaking letters.
   *
   * **Provenance:** populated ONLY at the wire-redaction boundary
   * (`redactStateForRole` for snapshots; `eventsToWire` builds it directly on
   * the WORD_HIDDEN frame for incremental events). The reducer never sets it,
   * so it is `undefined` in canonical reducer state and in the un-redacted
   * snapshot the drawer / audience-correct receive (they read `word` directly).
   */
  wordPattern?: string;
  tier: WordTier;
  /**
   * Wall-clock ms set at `PHASE_END(wordPick → drawing)`. `0` while in
   * wordPick (the picked word hasn't started "drawing" yet). Consumers
   * MUST treat `0` as "drawing has not started".
   */
  startedAt: number;
  /**
   * Append-only log of correct guesses for this turn. `points` is the value
   * returned by `audiencePointsForGuess(order, presentAudience)`. `atMs` is
   * the wall-clock ms from the `GUESS` action, `atMs - startedAt` gives the
   * latency consumed by TurnSummary's `firstGuessLatencyMs`.
   */
  guesses: ReadonlyArray<{
    playerId: PlayerId;
    points: number;
    order: number;
    atMs: number;
  }>;
  /**
   * Per-player count of reactions this turn (M4 batch 3, REACTION).
   *
   * PRD §3.3: "audience throws emoji onto the canvas, max 3 per audience
   * member per turn". The reducer enforces the 3/turn cap by reading this
   * map on each `REACTION` action and rejecting beyond 3. Initialized to
   * an empty Map at `PICK_WORD` and cleared (alongside the rest of
   * `currentTurn`) at the `drawing → reveal` transition.
   *
   * Visual layer only: reactions do NOT contribute to scoring or chat;
   * see PRD §3.3 / §13.1 ("REACTION never reaches the reducer if it would
   * affect game state"). The reducer's only job is to gate the rate limit.
   */
  reactionsByPlayer: ReadonlyMap<PlayerId, number>;
  /**
   * Letter positions revealed to audience-pending recipients (M4 batch 5
   *, hint scheduler). Replaced (not appended to) by each REVEAL_HINT,
   * the server is authoritative for the cumulative set. Initialized to
   * empty at PICK_WORD; cleared with the rest of `currentTurn` at
   * drawing → reveal.
   *
   * The set indexes into `word` directly. Positions over spaces /
   * punctuation are never inserted (see `cumulativeHintsThrough`);
   * clients render those positions as the literal character regardless
   * of the set membership.
   */
  revealedIndices: ReadonlySet<number>;
  /**
   * Index + character pairs for the positions in `revealedIndices`,
   * projected against the canonical `word` (M4 batch 6c). The
   * audience-pending recipient sees `word: ""` after snapshot redaction
   * (§5.12), so we carry the revealed letters out-of-band, that's
   * exactly enough information for the WordSkeleton to render
   * "_ _ _ c _ e _ _ _" without ever exposing the full word.
   *
   * Drawer + audience-correct see the full word already (no redaction),
   * but the field is populated for them too, same canonical projection
   * regardless of recipient. The wire layer / snapshot redactor are the
   * only places where role-based blanking happens.
   *
   * Order: `index` ascending (see `revealedLettersFromIndices`).
   * Stripped to `[]` for spectator recipients (matches the
   * `revealedIndices` empty-set treatment in `redactStateForRole`).
   */
  revealedLetters: ReadonlyArray<{ index: number; char: string }>;
  /**
   * Per-player count of GUESS actions this turn (V4.2.T10b flood cap). Mirrors
   * `reactionsByPlayer`: the reducer reads it on each GUESS and rejects past
   * `GUESS_RATE_LIMIT` so a single client can't flood the public wrong-guess
   * feed (PRD §5.12: there was no per-message guess throttle before, only IP
   * limiters + the REACTION 3/turn cap). Initialized empty at PICK_WORD,
   * cleared with the rest of `currentTurn` at drawing → reveal.
   *
   * **Server-runtime-only: NEVER serialized.** `serializeRoomState` omits it
   * (it carries no client-renderable info and we keep flood-control state off
   * the wire so it can't grow the snapshot or leak guess metadata). Optional so
   * snapshot-reconstructed turns (which never carry it) and legacy fixtures
   * stay valid; the reducer reads it with a `?? new Map()` default.
   */
  guessAttemptsByPlayer?: ReadonlyMap<PlayerId, number>;
  /**
   * Per-player NORMALIZED text of the last MISS guess this turn (V4.2.T10b
   * dedup). When a player repeats their previous miss verbatim (post-normalize)
   * the reducer suppresses the duplicate `CHAT_MESSAGE` so the feed doesn't
   * echo spam. Holds the normalized text of EVERY `miss`, including guesses
   * that failed the §5.12 token/length guard, since the dedup key is computed
   * unconditionally before the broadcast decision. It therefore may contain the
   * drawer's word; that is harmless ONLY because this map is never serialized
   * (see below) and never reaches the broadcast/backlog path.
   *
   * **Server-runtime-only: NEVER serialized.** `serializeRoomState` /
   * `serializeCurrentTurn` omit it; `redactStateForRole` preserves it on the
   * runtime state but it dies at the serializer boundary. Keeping it off the
   * wire is the invariant that must hold, it means a raw, possibly
   * word-revealing guess string can never ride out in a snapshot. Guarded by a
   * negative serializer test (snapshotWire.test.ts). Optional for the same
   * fixture/reconstruction reasons as `guessAttemptsByPlayer`.
   */
  lastMissByPlayer?: ReadonlyMap<PlayerId, string>;
};

export type GameState = {
  /** UUID v4 minted at START_GAME. Distinct from RoomId. Rematch mints fresh. */
  gameId: GameId;
  seed: number;
  rounds: number;
  currentRound: number;
  /**
   * Recomputed each round via `seedFor(gameId, "turnOrder", currentRound)`
   * AFTER `PROMOTE_SPECTATORS` has run, see §5.8. Players who joined as
   * spectators in round N enter the rotation cleanly in round N+1.
   */
  turnOrder: ReadonlyArray<PlayerId>;
  currentTurnIndex: number;
  turnSummaries: ReadonlyArray<TurnSummary>;
  scoring: ScoringConfig;
  hintsEnabled: boolean;
  drawerDisconnect?: DrawerDisconnect;
  /**
   * The 3-word slate offered to the current drawer. Set at PHASE_END entry
   * into `wordPick` (computed via `pickWordChoices`); cleared on any
   * transition leaving `wordPick`. Undefined when phase ∉ wordPick.
   */
  wordChoices?: readonly [WordChoice, WordChoice, WordChoice];
  /**
   * Live per-turn state. Set by `PICK_WORD` (drawer picks from
   * `wordChoices`); `startedAt` patched at `PHASE_END(wordPick → drawing)`;
   * appended by correct `GUESS`es; cleared at `PHASE_END(drawing → reveal)`
   * after a `TurnSummary` is appended to `turnSummaries`.
   */
  currentTurn?: CurrentTurn;
};

// ---------------------------------------------------------------------------
// FreeDrawState (populated when kind === "freeDraw")
// ---------------------------------------------------------------------------

export type FreeDrawState = {
  creatorId: PlayerId;
};

// ---------------------------------------------------------------------------
// RoomState (top-level)
// ---------------------------------------------------------------------------

export type RoomState = {
  id: RoomId;
  kind: RoomKind;
  phase: RoomPhase;
  hostId: PlayerId;
  players: ReadonlyMap<PlayerId, Player>;
  spectators: ReadonlySet<PlayerId>;
  settings: RoomSettings;
  game?: GameState; // kind === "game" && phase !== "lobby"
  freeDraw?: FreeDrawState; // kind === "freeDraw"
  openStrokes: ReadonlyMap<StrokeId, OpenStroke>;
  phaseStartedAt: number;
};

// ---------------------------------------------------------------------------
// Snapshot type (used by RoomRuntime.snapshotFor + ROOM_STATE_SYNC payload).
// ---------------------------------------------------------------------------

export type RoomSnapshot = {
  state: RoomState;
  msRemaining: number;
};
